// The practice games as the learner meets them: icon, Hindi + English name, one line, and what each level means.

import type { LucideIcon } from 'lucide-react'
import type { L } from '@/prakrit/lib/lessons/types'
import type {
    Level,
    PracticeMode,
    SessionMode,
    Unavailable
} from '@/prakrit/components/practice/practice-types'

import {
    ArrowRightLeft,
    CalendarCheck,
    Clock,
    Combine,
    Grid3x3,
    Headphones,
    Languages,
    Layers,
    ListOrdered,
    Puzzle,
    Shapes,
    Sprout,
    Tag,
    Users
} from 'lucide-react'

export interface ModeInfo {
    mode: PracticeMode
    icon: LucideIcon
    name: L
    line: L
    /** A shorter, friendlier line for Kids mode. */
    kidsLine: L
    /** What changes from level to level in this game. */
    levels: Record<Level, L>
    /** Part of the friendlier set shown first in Kids mode. */
    kids: boolean
}

const TENSE_LEVELS: Record<Level, L> = {
    arambh: { hi: 'केवल वर्तमान काल', en: 'present only' },
    abhyas: { hi: 'वर्तमान, विधि-आज्ञा, भूत', en: 'present, command, past' },
    praveen: { hi: 'चारों काल', en: 'all four tenses' }
}

const OPTION_LEVELS: Record<Level, L> = {
    arambh: { hi: 'सरल, कम विकल्प', en: 'simple, fewer choices' },
    abhyas: { hi: 'उल्टी दिशा भी', en: 'both directions' },
    praveen: { hi: 'सब प्रकार', en: 'everything' }
}

export const MODES: ModeInfo[] = [
    {
        mode: 'dhatu-chunauti',
        icon: Sprout,
        name: { hi: 'धातु चुनौती', en: 'Root challenge' },
        line: { hi: 'कर्ता, धातु और काल देखकर सही क्रिया-रूप चुनिए।', en: 'See a subject, a root and a tense; choose the verb form.' },
        kidsLine: { hi: 'कौन क्या करता है? सही शब्द चुनो!', en: 'Who does what? Pick the right word!' },
        levels: TENSE_LEVELS,
        kids: true
    },
    {
        mode: 'vakya-nirmata',
        icon: Puzzle,
        name: { hi: 'वाक्य निर्माता', en: 'Build a sentence' },
        line: { hi: 'शब्द-टाइलों से अर्थ के अनुसार प्राकृत वाक्य बनाइए — कोई भी सही क्रम चलेगा।', en: 'Build the Prakrit sentence for a meaning from word tiles; any correct order works.' },
        kidsLine: { hi: 'शब्दों को जोड़कर वाक्य बनाओ!', en: 'Join the words into a sentence!' },
        levels: {
            arambh: { hi: 'वर्तमान: कर्ता + क्रिया', en: 'present: subject + verb' },
            abhyas: { hi: 'तीन काल, कर्म के साथ', en: 'three tenses, with an object' },
            praveen: { hi: 'चारों काल, अव्यय भी', en: 'all tenses, adverbs too' }
        },
        kids: true
    },
    {
        mode: 'vachan-badlo',
        icon: Users,
        name: { hi: 'वचन बदलो', en: 'Change the number' },
        line: { hi: 'एकवचन वाक्य का बहुवचन चुनिए — और उल्टा भी।', en: 'Turn a singular sentence into the plural, and back.' },
        kidsLine: { hi: 'एक से बहुत! वाक्य बदलो।', en: 'One to many! Change the sentence.' },
        levels: TENSE_LEVELS,
        kids: true
    },
    {
        mode: 'anuvad',
        icon: Languages,
        name: { hi: 'अनुवाद', en: 'Translate' },
        line: { hi: 'हिन्दी या अंग्रेज़ी अर्थ के लिए सही प्राकृत वाक्य चुनिए।', en: 'Choose the Prakrit sentence for a Hindi or English meaning.' },
        kidsLine: { hi: 'प्राकृत में कैसे कहेंगे? चुनो!', en: 'How do we say it in Prakrit? Choose!' },
        levels: TENSE_LEVELS,
        kids: true
    },
    {
        mode: 'katha-kram',
        icon: ListOrdered,
        name: { hi: 'कथा क्रम', en: 'Story order' },
        line: { hi: 'कथा की पंक्तियों को फिर से सही क्रम में लगाइए।', en: 'Put the lines of a story back in order.' },
        kidsLine: { hi: 'कहानी को सही क्रम में लगाओ!', en: 'Put the story in order!' },
        levels: {
            arambh: { hi: '३ पंक्तियाँ, अर्थ के साथ', en: '3 lines, with meanings' },
            abhyas: { hi: '४ पंक्तियाँ, अर्थ के साथ', en: '4 lines, with meanings' },
            praveen: { hi: '५ पंक्तियाँ, बिना अर्थ', en: '5 lines, no meanings' }
        },
        kids: true
    },
    {
        mode: 'shravan',
        icon: Headphones,
        name: { hi: 'श्रवण अभ्यास', en: 'Listening' },
        line: { hi: 'शिक्षक की रिकॉर्डिंग या डिवाइस की आवाज़ सुनिए और जो सुना, वह चुनिए।', en: "Listen to a teacher's recording or your device's voice, and choose what you heard." },
        kidsLine: { hi: 'सुनो और सही शब्द चुनो!', en: 'Listen and pick the right word!' },
        levels: {
            arambh: { hi: 'कम विकल्प', en: 'fewer choices' },
            abhyas: { hi: 'अधिक विकल्प', en: 'more choices' },
            praveen: { hi: 'अधिक विकल्प', en: 'more choices' }
        },
        kids: true
    },
    {
        mode: 'kaal-pahchan',
        icon: Clock,
        name: { hi: 'काल पहचान', en: 'Which tense?' },
        line: { hi: 'एक क्रिया-रूप देखिए और बताइए कि वह किस काल का है।', en: 'Look at a verb form and name its tense.' },
        kidsLine: { hi: 'यह कब हुआ? काल पहचानो!', en: 'When does it happen? Spot the tense!' },
        levels: {
            arambh: { hi: 'तीन काल, अधिकतर एकवचन', en: 'three tenses, mostly singular' },
            abhyas: { hi: 'तीन काल, दोनों वचन', en: 'three tenses, both numbers' },
            praveen: { hi: 'चारों काल', en: 'all four tenses' }
        },
        kids: false
    },
    {
        mode: 'roop-sadhana',
        icon: Grid3x3,
        name: { hi: 'रूप साधना', en: 'Complete the table' },
        line: { hi: '३ × २ क्रिया-तालिका के खाली खाने भरिए।', en: 'Fill the missing cells of a 3 × 2 verb table.' },
        kidsLine: { hi: 'तालिका के खाली खाने भरो!', en: 'Fill the empty boxes of the table!' },
        levels: {
            arambh: { hi: 'वर्तमान, एक खाना', en: 'present, one cell' },
            abhyas: { hi: 'वर्तमान, विधि · दो खाने', en: 'present, command · two cells' },
            praveen: { hi: 'भविष्यत् भी · तीन खाने', en: 'future too · three cells' }
        },
        kids: false
    },
    {
        mode: 'kridanta-pahchan',
        icon: Tag,
        name: { hi: 'कृदन्त पहचान', en: 'Spot the participle' },
        line: { hi: 'कृदन्त-रूप देखकर उसका प्रकार बताइए, या धातु और प्रकार से सही रूप चुनिए।', en: 'See a participle and name its kind, or choose the form for a root and a kind.' },
        kidsLine: { hi: 'करके, करने के लिए: रूप पहचानो!', en: 'Having done, in order to: spot the form!' },
        levels: {
            arambh: { hi: 'सम्बन्ध और हेत्वर्थ कृदन्त', en: 'having done, in order to' },
            abhyas: { hi: 'वर्तमान और भूत कृदन्त भी, दोनों दिशाएँ', en: 'present and past participles too, both ways' },
            praveen: { hi: 'छहों कृदन्त', en: 'all six kinds' }
        },
        kids: false
    },
    {
        mode: 'vibhakti-chayan',
        icon: Shapes,
        name: { hi: 'विभक्ति चयन', en: 'Choose the case' },
        line: { hi: 'वाक्य में भूमिका (कर्ता, कर्म …) के लिए संज्ञा का सही रूप चुनिए।', en: 'Pick the noun form for a role in the sentence (subject, object …).' },
        kidsLine: { hi: 'शब्द का सही रूप चुनो!', en: 'Pick the right form of the word!' },
        levels: {
            arambh: { hi: 'प्रथमा और द्वितीया', en: 'nominative and accusative' },
            abhyas: { hi: 'सात विभक्तियाँ', en: 'seven cases' },
            praveen: { hi: 'आठों विभक्तियाँ', en: 'all eight cases' }
        },
        kids: false
    },
    {
        mode: 'sanskrit-prakrit',
        icon: ArrowRightLeft,
        name: { hi: 'संस्कृत → प्राकृत', en: 'Sanskrit to Prakrit' },
        line: { hi: 'संस्कृत शब्द प्राकृत में कैसे बदलता है? पुस्तक के युग्मों से।', en: 'How does a Sanskrit word change in Prakrit? Pairs from the book.' },
        kidsLine: { hi: 'शब्द कैसे बदलता है? चुनो!', en: 'How does the word change? Choose!' },
        levels: OPTION_LEVELS,
        kids: false
    },
    {
        mode: 'sandhi-nirmata',
        icon: Combine,
        name: { hi: 'सन्धि निर्माता', en: 'Join and split' },
        line: { hi: 'दो शब्दों को सन्धि से जोड़िए, या सन्धि-विच्छेद कीजिए।', en: 'Join two words with sandhi, or split them apart.' },
        kidsLine: { hi: 'दो शब्द मिलाओ!', en: 'Join two words!' },
        levels: {
            arambh: { hi: 'स्वर सन्धि, जोड़ना', en: 'vowel sandhi, joining' },
            abhyas: { hi: 'सब सन्धियाँ, विच्छेद भी', en: 'all kinds, splitting too' },
            praveen: { hi: 'सब सन्धियाँ, विच्छेद भी', en: 'all kinds, splitting too' }
        },
        kids: false
    },
    {
        mode: 'samas-pahchan',
        icon: Layers,
        name: { hi: 'समास पहचान', en: 'Name the compound' },
        line: { hi: 'समास और उसका विग्रह देखकर समास का प्रकार पहचानिए।', en: 'Read a compound and its analysis; name the kind of compound.' },
        kidsLine: { hi: 'जुड़ा हुआ शब्द पहचानो!', en: 'Spot the kind of joined word!' },
        levels: {
            arambh: { hi: 'तीन विकल्प', en: 'three choices' },
            abhyas: { hi: 'चार विकल्प', en: 'four choices' },
            praveen: { hi: 'चार विकल्प', en: 'four choices' }
        },
        kids: false
    }
]

export const modeInfo = (mode: PracticeMode): ModeInfo => MODES.find(m => m.mode === mode) as ModeInfo

/** आज का अभ्यास as a pseudo-mode for headings. */
export const MIXED_INFO = {
    icon: CalendarCheck,
    name: { hi: 'आज का अभ्यास', en: "Today's practice" },
    line: { hi: 'दोहराव के लिए तैयार रूप पहले, फिर अलग-अलग खेलों से मिला-जुला अभ्यास।', en: 'Items due for review first, then a mix of games.' }
}

export const sessionName = (mode: SessionMode): L => (mode === 'aaj' ? MIXED_INFO.name : modeInfo(mode).name)

export const LEVEL_INFO: Record<Level, { name: L, numeral: string, line: L }> = {
    arambh: {
        name: { hi: 'आरम्भ', en: 'Beginning' },
        numeral: '१',
        line: { hi: 'केवल वर्तमान काल, तीन विकल्प।', en: 'Present tense only, three choices.' }
    },
    abhyas: {
        name: { hi: 'अभ्यास', en: 'Practice' },
        numeral: '२',
        line: { hi: 'वर्तमान, विधि-आज्ञा और भूत, चार विकल्प।', en: 'Present, command and past, four choices.' }
    },
    praveen: {
        name: { hi: 'प्रवीण', en: 'Expert' },
        numeral: '३',
        line: { hi: 'चारों काल। वयस्कों के लिए हर प्रश्न-भाग के 20 सेकंड।', en: 'All four tenses. For Adults, 20 seconds per question part.' }
    }
}

/** Short Hindi + English names of the unavailable reasons. */
export const UNAVAILABLE: Record<Unavailable, L> = {
    no_verified: { hi: 'यह अभ्यास जल्द खुलेगा', en: 'This practice opens soon' },
    no_recordings: { hi: 'न रिकॉर्डिंग है, न इस डिवाइस पर हिन्दी आवाज़', en: 'No recordings yet, and no Hindi voice on this device' },
    no_stories: { hi: 'अभी कोई प्रकाशित कथा नहीं', en: 'No published stories are available yet' },
    no_focus: { hi: 'यह शब्द अभी अभ्यास के लिए उपलब्ध नहीं', en: 'This word is not available for practice yet' },
    no_topic: { hi: 'चुने गए विषयों का कोई प्रश्न अभी नहीं', en: 'No questions for the chosen topics yet' },
    needs_plan: { hi: 'यह सेट प्रीमियम में है', en: 'This set is Premium' }
}

/** Why श्रवण is closed on this device although the device voice would open it. */
export const VOICE_MISSING: Record<'off' | 'none', L> = {
    off: { hi: 'रिकॉर्डिंग अभी नहीं हैं, और डिवाइस की आवाज़ बन्द है।', en: 'There are no recordings yet, and the device voice is switched off.' },
    none: { hi: 'रिकॉर्डिंग अभी नहीं हैं, और इस ब्राउज़र में हिन्दी आवाज़ नहीं मिली। दूसरे ब्राउज़र या डिवाइस में आज़माइए।', en: 'There are no recordings yet, and this browser has no Hindi voice. Try another browser or device.' }
}
